import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { useOwnerApi, commercialError } from '../commercial/useCommercialApi';
import { usd, primaryLabel } from '../commercial/commercialFormat';

/**
 * GOV13 Section E — SuperAdmin Add-ons & Usage-Packs authoring (governed).
 *
 * Backend-authoritative FRONTEND ONLY. Consumes the EXISTING owner commercial routes
 * (/api/v1/super-admin/commercial/*) — it creates NO route and is NOT a second authority. Lifecycle actions
 * are rendered STRICTLY from each version's backend-provided `allowed_actions` (never a hardcoded transition
 * graph), exactly like TenantCommercialPanel. Every mutation disables while pending (no duplicate submit); on
 * success shows a governed toast + refresh; on HTTP 503 shows the truthful amber "not enabled in this
 * environment" banner (never a false success); on any other error renders the EXACT backend rejection code.
 * Nothing auto-publishes; new headers/versions default to DRAFT; every lifecycle action sends a reason; no
 * Stripe object is created. Published/immutable versions render read-only with an explanation.
 */

// Governed catalog types/models the backend accepts (superadmin catalog_admin_routes). Rendered as selects so
// the owner picks valid values; the backend re-validates regardless.
const ADDON_TYPES = ['PREMIUM_MODULE', 'FEATURE_GRANT', 'INTEGRATION_GRANT', 'LIMIT_INCREASE',
  'RECURRING_CREDIT_ALLOWANCE', 'ONE_TIME_CREDIT_PACK', 'SETUP_SERVICE', 'STORAGE_PACK', 'STAFF_PACK',
  'MARKETPLACE_PREMIUM', 'SCHEDULING_PREMIUM', 'CUSTOM_ENTERPRISE'];
const BILLING_MODELS = ['ONE_TIME', 'MONTHLY_RECURRING', 'ANNUAL_RECURRING', 'USAGE_BASED', 'CREDIT_PACK',
  'QUANTITY_BASED', 'CUSTOM_INVOICE', 'OWNER_GRANTED_NO_CHARGE'];

// allowed_actions keys that are governed LIFECYCLE transitions → the `to` state posted to /lifecycle. Only the
// keys the backend marks truthy are rendered (edit/validate/immutable are handled separately, not here).
const LIFECYCLE_TO = { submit: 'under_review', approve: 'approved', publish: 'published', supersede: 'superseded', retire: 'retired' };
const LIFECYCLE_LABEL = { submit: 'Submit for review', approve: 'Approve', publish: 'Publish', supersede: 'Supersede', retire: 'Retire' };

const LC_TONE = {
  draft: 'bg-gray-100 text-gray-700', under_review: 'bg-blue-100 text-blue-800', approved: 'bg-indigo-100 text-indigo-800',
  scheduled: 'bg-purple-100 text-purple-800', published: 'bg-green-100 text-green-800',
  superseded: 'bg-gray-200 text-gray-600', retired: 'bg-gray-200 text-gray-500',
};
const Pill = ({ v }) => <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${LC_TONE[v] || 'bg-gray-100 text-gray-600'}`}>{v || '—'}</span>;

const intOrNull = (s) => (s === '' || s == null ? null : (Number.isFinite(parseInt(s, 10)) ? parseInt(s, 10) : null));
const commaList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

const emptyAddon = { id: '', display: '', price_cents: '', cadence: 'recurring', reason: '' };
const emptyVersion = {
  addon_type: '', billing_model: '', billing_cadence: '', currency: 'USD', unit_price_cents: '', annual_price_cents: '',
  eligible_plan_ids: '', tenant_requestable: false, manual_invoice_eligible: false, self_service_purchasable: false,
  public_description: '', effective_at: '', reason: '',
  // Generic capacity grants → persisted as commercial_add_on_versions.limit_grants { limit_key: +delta }. The
  // resolver ADDS delta*quantity onto the base plan limit. Rows are the ONLY authority — never derived from id/name.
  limit_grants_rows: [],
  // Generic boolean feature grants → commercial_add_on_versions.feature_grants ["feature_key", …] (e.g. tracking_sms).
  // Sourced from the canonical Feature Catalog (boolean features) — no hardcoded key.
  feature_grants: [],
};
const emptyPack = { pack_key: '', bucket: '', credits: '', price: '', currency: 'USD', expiry_months: '12', reason: '' };

// Governed CAPACITY-LIMIT usage packs (NOT a credit wallet). limit_key is the metered capacity being raised.
const USAGE_LIMIT_KEYS = ['monthly_engagements', 'monthly_ai_resolutions', 'max_staff', 'active_integrations', 'storage_gb', 'locations'];
const emptyUsagePack = {
  product_key: '', display_name: '', description: '', limit_key: '', unit: '', granted_quantity: '',
  price: '', currency: 'USD', expiry_months: '12', eligible_plan_versions: '', eligible_profiles: '', reason: '',
};
const emptyStripeMap = { stripe_test_product_id: '', stripe_test_price_id: '' };

const READ_ONLY_MSG = 'Read only — create a new draft to change this version';

export default function AddonPackAuthoring() {
  const { superAdmin } = useSuperAdminAuth();
  const api = useOwnerApi(superAdmin);

  const [addOns, setAddOns] = useState([]);
  const [packVersions, setPackVersions] = useState([]);
  const [usagePackVersions, setUsagePackVersions] = useState([]);   // governed CAPACITY-LIMIT usage packs
  const [limitKeys, setLimitKeys] = useState([]);   // canonical category=limit features (Feature Catalog is the SOLE registry)
  const [featureGrantKeys, setFeatureGrantKeys] = useState([]);   // canonical BOOLEAN features grantable by an add-on
  const [addOnVersions, setAddOnVersions] = useState({});   // { [add_on_id]: versions[] }
  const [expanded, setExpanded] = useState(() => new Set());
  const [validateResults, setValidateResults] = useState({}); // { [key]: {valid, errors} }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [gated, setGated] = useState(false);                // HTTP 503 write-gate banner
  const [busy, setBusy] = useState(false);                  // duplicate-submission guard

  // form panel: { kind, addOnId?, version? } and its field state
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState({});

  // reason modal for lifecycle transitions
  const [reasonModal, setReasonModal] = useState(null);     // { title, doRun }
  const [reasonText, setReasonText] = useState('');
  const [reasonErr, setReasonErr] = useState(null);

  const loadTop = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [a, p, u, lf] = await Promise.allSettled([
        api.get('/api/v1/super-admin/commercial/add-ons'),
        api.get('/api/v1/super-admin/commercial/catalog/credit-packs/versions'),
        api.get('/api/v1/super-admin/commercial/catalog/usage-packs/versions'),
        // Canonical Feature Catalog (SOLE registry) — full catalogue drives BOTH the limit-key options
        // (category=limit) and the boolean feature-grant options.
        api.get('/api/v1/super-admin/commercial/features', { params: { page_size: 1000 } }),
      ]);
      if (a.status === 'rejected' && p.status === 'rejected' && u.status === 'rejected') throw a.reason || new Error('load failed');
      setAddOns(a.status === 'fulfilled' ? (a.value.data?.add_ons || []) : []);
      setPackVersions(p.status === 'fulfilled' ? (p.value.data?.versions || []) : []);
      setUsagePackVersions(u.status === 'fulfilled' ? (u.value.data?.versions || []) : []);
      const lfItems = lf.status === 'fulfilled' ? (lf.value.data?.items || lf.value.data?.features || []) : [];
      setLimitKeys(lfItems.filter((x) => (x.category === 'limit') && x.feature_key)
        .map((x) => ({ key: x.feature_key, label: x.display_name || x.feature_key })));
      setFeatureGrantKeys(lfItems.filter((x) => x.feature_key && x.entitlement_type === 'boolean')
        .map((x) => ({ key: x.feature_key, label: x.display_name || x.feature_key })));
    } catch (e) { setError(commercialError(e)); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { loadTop(); }, [loadTop]);

  const loadVersions = useCallback(async (addOnId) => {
    try {
      const r = await api.get(`/api/v1/super-admin/commercial/add-ons/${encodeURIComponent(addOnId)}/versions`);
      setAddOnVersions((m) => ({ ...m, [addOnId]: r.data?.versions || [] }));
    } catch (e) { setToast({ ok: false, msg: commercialError(e) }); }
  }, [api]);

  const toggleVersions = async (addOnId) => {
    const willOpen = !expanded.has(addOnId);
    setExpanded((s) => { const n = new Set(s); if (n.has(addOnId)) n.delete(addOnId); else n.add(addOnId); return n; });
    if (willOpen && !addOnVersions[addOnId]) await loadVersions(addOnId);
  };

  // Eagerly load versions for every add-on so each row can show the EFFECTIVE (published) version state, not just
  // the header's own lifecycle (which stays 'draft' forever even after a version publishes). This corrects the
  // misleading "draft" badge on products that actually have a published, tenant-requestable version.
  useEffect(() => {
    addOns.forEach((a) => { if (a?.id && addOnVersions[a.id] === undefined) loadVersions(a.id); });
    // eslint-disable-next-line
  }, [addOns]);

  const addonEffective = (id) => {
    const vs = addOnVersions[id];
    if (!vs) return null;
    const pub = vs.filter((v) => v.lifecycle_state === 'published');
    const eff = pub.length ? pub.reduce((x, y) => (y.version > x.version ? y : x)) : null;
    const latest = vs.length ? vs.reduce((x, y) => (y.version > x.version ? y : x)) : null;
    return { eff, latest };
  };

  // ONE governed action dispatcher. Disables while pending; 503 → truthful gated banner (never a false
  // success); other errors → EXACT backend rejection code. Never an optimistic success.
  const run = async (fn, okMsg, after) => {
    if (busy) return;
    setBusy(true); setToast(null); setGated(false);
    try {
      await fn();
      setToast({ ok: true, msg: okMsg });
      if (after) await after();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) { setToast({ ok: false, msg: 'Your SuperAdmin session expired. Please sign in again.' }); return; }
      if (status === 503) { setGated(true); return; }
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      const det = e?.response?.data?.detail?.detail || e?.response?.data?.detail;
      const errs = det?.errors ? ` (${det.errors.map((x) => x.code || x.error || x).join(', ')})` : '';
      setToast({ ok: false, msg: code ? `Rejected: ${code}${errs}` : 'Action failed.' });
    } finally { setBusy(false); }
  };

  const openForm = (kind, extra = {}, initial = {}) => { setForm({ kind, ...extra }); setFields(initial); setToast(null); };
  const closeForm = () => { setForm(null); setFields({}); };

  // ── mutations ──────────────────────────────────────────────────────────────
  const submitAddon = () => {
    if (!fields.id?.trim()) { setToast({ ok: false, msg: 'An internal id/key is required.' }); return; }
    const body = {
      id: fields.id.trim(), display: fields.display?.trim() || fields.id.trim(),
      price_cents: intOrNull(fields.price_cents), cadence: fields.cadence || 'recurring',
      grants: {}, reason: fields.reason?.trim() || undefined,
    };
    run(() => api.post('/api/v1/super-admin/commercial/add-ons', body), `Add-on "${body.display}" created as a DRAFT header.`,
      async () => { closeForm(); await loadTop(); });
  };

  // Assemble limit_grants { limit_key: +delta } from the editor rows. Only valid canonical keys with a POSITIVE
  // integer delta are kept; the backend re-validates (add_on_type=LIMIT_INCREASE requires a non-empty grant).
  const buildLimitGrants = (rows) => {
    const out = {};
    for (const r of (rows || [])) {
      const k = (r?.key || '').trim();
      const d = parseInt(r?.delta, 10);
      if (k && Number.isFinite(d) && d > 0) out[k] = d;
    }
    return out;
  };

  const versionBody = (f) => ({
    addon_type: f.addon_type || null, billing_model: f.billing_model || null, billing_cadence: f.billing_cadence || null,
    currency: (f.currency || 'USD').toUpperCase(), unit_price_cents: intOrNull(f.unit_price_cents),
    annual_price_cents: intOrNull(f.annual_price_cents), eligible_plan_ids: commaList(f.eligible_plan_ids),
    tenant_requestable: !!f.tenant_requestable, manual_invoice_eligible: !!f.manual_invoice_eligible,
    self_service_purchasable: !!f.self_service_purchasable, public_description: f.public_description?.trim() || '',
    effective_at: f.effective_at || null, limit_grants: buildLimitGrants(f.limit_grants_rows),
    feature_grants: Array.isArray(f.feature_grants) ? f.feature_grants : [],
    reason: f.reason?.trim() || undefined,
  });

  const submitVersion = (addOnId) => {
    run(() => api.post(`/api/v1/super-admin/commercial/add-ons/${encodeURIComponent(addOnId)}/versions`, versionBody(fields)),
      'Draft add-on version created.',
      async () => { closeForm(); setExpanded((s) => new Set(s).add(addOnId)); await loadVersions(addOnId); });
  };

  const submitEditVersion = (addOnId, version) => {
    run(() => api.patch(`/api/v1/super-admin/commercial/add-ons/${encodeURIComponent(addOnId)}/versions/${version}`, versionBody(fields)),
      `Draft v${version} updated.`,
      async () => { closeForm(); await loadVersions(addOnId); });
  };

  const submitPack = () => {
    if (!fields.pack_key?.trim()) { setToast({ ok: false, msg: 'A pack_key is required.' }); return; }
    const body = {
      bucket: fields.bucket?.trim() || null, credits: intOrNull(fields.credits), price: intOrNull(fields.price),
      currency: (fields.currency || 'USD').toUpperCase(), expiry_months: intOrNull(fields.expiry_months),
      reason: fields.reason?.trim() || undefined,
    };
    run(() => api.post(`/api/v1/super-admin/commercial/catalog/credit-packs/${encodeURIComponent(fields.pack_key.trim())}/versions`, body),
      'Governed credit-pack draft version created.',
      async () => { closeForm(); await loadTop(); });
  };

  const importSeedPacks = () => run(
    () => api.post('/api/v1/super-admin/commercial/catalog/credit-packs/import-seed', {}),
    'Seed credit packs imported as governed DRAFT versions.', loadTop);

  const validateAddonVersion = (addOnId, version) => run(async () => {
    const r = await api.post(`/api/v1/super-admin/commercial/add-ons/${encodeURIComponent(addOnId)}/versions/${version}/validate`, {});
    setValidateResults((m) => ({ ...m, [`a:${addOnId}:${version}`]: r.data }));
  }, `Validated add-on v${version}.`);

  const validatePackVersion = (packKey, version) => run(async () => {
    const r = await api.post(`/api/v1/super-admin/commercial/catalog/credit-packs/${encodeURIComponent(packKey)}/versions/${version}/validate`, {});
    setValidateResults((m) => ({ ...m, [`p:${packKey}:${version}`]: r.data }));
  }, `Validated pack v${version}.`);

  const askReason = (title, doRun) => { setReasonModal({ title, doRun }); setReasonText(''); setReasonErr(null); };
  const confirmReason = () => {
    const r = reasonText.trim();
    if (!r) { setReasonErr('A reason is required (recorded in the audit log).'); return; }
    const m = reasonModal; setReasonModal(null); m.doRun(r);
  };

  const addonLifecycle = (addOnId, version, actionKey) => {
    const to = LIFECYCLE_TO[actionKey];
    askReason(`${LIFECYCLE_LABEL[actionKey]} add-on v${version}`, (reason) => run(
      () => api.post(`/api/v1/super-admin/commercial/add-ons/${encodeURIComponent(addOnId)}/versions/${version}/lifecycle`, { to, reason }),
      `Add-on v${version} → ${to}.`, async () => { await loadVersions(addOnId); }));
  };
  const packLifecycle = (packKey, version, actionKey) => {
    const to = LIFECYCLE_TO[actionKey];
    askReason(`${LIFECYCLE_LABEL[actionKey]} pack v${version}`, (reason) => run(
      () => api.post(`/api/v1/super-admin/commercial/catalog/credit-packs/${encodeURIComponent(packKey)}/versions/${version}/lifecycle`, { to, reason }),
      `Pack v${version} → ${to}.`, loadTop));
  };

  // ── USAGE_PACK (capacity-limit) mutations — governed, NEW backend routes ────────
  const usagePackBody = (f) => ({
    display_name: f.display_name?.trim() || undefined,
    description: f.description?.trim() || undefined,
    limit_key: f.limit_key || null,
    unit: f.unit?.trim() || null,
    granted_quantity: intOrNull(f.granted_quantity),
    price: intOrNull(f.price),
    currency: (f.currency || 'USD').toUpperCase(),
    expiry_months: intOrNull(f.expiry_months),
    eligible_plan_versions: commaList(f.eligible_plan_versions),
    eligible_profiles: commaList(f.eligible_profiles),
    reason: f.reason?.trim() || undefined,
  });

  const submitUsagePack = () => {
    if (!fields.product_key?.trim()) { setToast({ ok: false, msg: 'A product_key is required.' }); return; }
    if (!fields.limit_key) { setToast({ ok: false, msg: 'A capacity limit_key is required.' }); return; }
    const gq = intOrNull(fields.granted_quantity);
    if (gq == null || gq <= 0) { setToast({ ok: false, msg: 'Granted quantity must be a positive integer.' }); return; }
    run(() => api.post(`/api/v1/super-admin/commercial/catalog/usage-packs/${encodeURIComponent(fields.product_key.trim())}/versions`, usagePackBody(fields)),
      'Governed usage-pack (capacity-limit) draft version created.',
      async () => { closeForm(); await loadTop(); });
  };

  const submitEditUsagePack = (productKey, version) => {
    run(() => api.patch(`/api/v1/super-admin/commercial/catalog/usage-packs/${encodeURIComponent(productKey)}/versions/${version}`, usagePackBody(fields)),
      `Draft usage-pack v${version} updated.`,
      async () => { closeForm(); await loadTop(); });
  };

  const importSeedUsagePacks = () => run(
    () => api.post('/api/v1/super-admin/commercial/catalog/usage-packs/import-seed', {}),
    'Seed packs imported as governed DRAFT versions.', loadTop);

  const validateUsagePackVersion = (productKey, version) => run(async () => {
    const r = await api.post(`/api/v1/super-admin/commercial/catalog/usage-packs/${encodeURIComponent(productKey)}/versions/${version}/validate`, {});
    setValidateResults((m) => ({ ...m, [`u:${productKey}:${version}`]: r.data }));
  }, `Validated usage-pack v${version}.`);

  const usagePackLifecycle = (productKey, version, actionKey) => {
    const to = LIFECYCLE_TO[actionKey];
    askReason(`${LIFECYCLE_LABEL[actionKey]} usage-pack v${version}`, (reason) => run(
      () => api.post(`/api/v1/super-admin/commercial/catalog/usage-packs/${encodeURIComponent(productKey)}/versions/${version}/lifecycle`, { to, reason }),
      `Usage-pack v${version} → ${to}.`, loadTop));
  };

  // ── Stripe TEST mapping — attaches an EXISTING Stripe TEST product/price to a governed version.
  // scope ∈ 'credit-packs' | 'usage-packs'. Creates NO Stripe object; TEST-mode reference only.
  const submitStripeMap = (scope, key, version) => {
    const body = {
      stripe_test_product_id: fields.stripe_test_product_id?.trim() || null,
      stripe_test_price_id: fields.stripe_test_price_id?.trim() || null,
    };
    if (!body.stripe_test_product_id || !body.stripe_test_price_id) {
      setToast({ ok: false, msg: 'Both the Stripe TEST product id and price id are required.' }); return;
    }
    run(() => api.post(`/api/v1/super-admin/commercial/catalog/${scope}/${encodeURIComponent(key)}/versions/${version}/stripe-map`, body),
      `Stripe TEST mapping saved for v${version}.`,
      async () => { closeForm(); await loadTop(); });
  };

  // ── shared render helpers ────────────────────────────────────────────────────
  const LifecycleButtons = ({ allowed, onAct }) => (
    <>
      {Object.entries(allowed || {}).filter(([k, ok]) => ok && LIFECYCLE_TO[k]).map(([k]) => (
        <button key={k} disabled={busy} onClick={() => onAct(k)}
          className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">{LIFECYCLE_LABEL[k]}</button>
      ))}
    </>
  );
  const ValidateResult = ({ res }) => {
    if (!res) return null;
    return res.valid
      ? <div className="mt-1 text-xs text-green-700">Valid — passes governance checks.</div>
      : <div className="mt-1 text-xs text-red-700">Invalid: {(res.errors || []).map((e) => e.code || e.error || String(e)).join(', ') || 'see backend'}</div>;
  };

  const th = (h) => <th key={h} className="px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>;
  const input = (key, props = {}) => (
    <input className="mt-1 w-full border rounded px-2 py-1 text-sm" value={fields[key] ?? ''}
      onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))} {...props} />
  );
  const check = (key, label) => (
    <label className="flex items-center gap-2 text-xs text-gray-700">
      <input type="checkbox" checked={!!fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.checked }))} />{label}
    </label>
  );
  const selectField = (key, options) => (
    <select className="mt-1 w-full border rounded px-2 py-1 text-sm" value={fields[key] ?? ''} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}>
      <option value="">— select —</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  // Generic capacity-limit grant editor → commercial_add_on_versions.limit_grants { limit_key: +delta }. Target
  // options come ONLY from the canonical Feature Catalog (category=limit); delta is a positive integer. Fully
  // generic — no hardcoded vehicles/trackers/Movers. A plain render fn (not a component) so typing never remounts.
  const limitGrantsEditor = () => {
    const rows = fields.limit_grants_rows || [];
    const setRows = (next) => setFields((f) => ({ ...f, limit_grants_rows: next }));
    const isLimitIncrease = String(fields.addon_type || '').toUpperCase() === 'LIMIT_INCREASE';
    return (
      <div className="mt-3 border-t pt-2">
        <div className="text-xs font-semibold text-gray-700 mb-1">
          Capacity limit increases <span className="font-normal text-gray-400">(adds +delta onto the tenant’s base plan limit × purchased quantity)</span>
          {isLimitIncrease && rows.length === 0 && <span className="ml-2 text-amber-700">— required for a LIMIT_INCREASE add-on</span>}
        </div>
        {rows.length === 0 && <p className="text-xs text-gray-400 mb-1">No capacity grant yet. Add one so this add-on actually raises a limit.</p>}
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <select aria-label={`Target limit ${i + 1}`} className="border rounded px-2 py-1 text-sm w-64"
              value={r.key || ''} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}>
              <option value="">— target limit —</option>
              {limitKeys.map((o) => <option key={o.key} value={o.key}>{o.label} ({o.key})</option>)}
            </select>
            <span className="text-xs text-gray-500">+</span>
            <input aria-label={`Increase by ${i + 1}`} type="number" min="1" step="1" placeholder="delta"
              className="border rounded px-2 py-1 text-sm w-24" value={r.delta ?? ''}
              onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, delta: e.target.value } : x)))} />
            <button type="button" className="text-xs text-red-600 hover:underline"
              onClick={() => setRows(rows.filter((_, j) => j !== i))}>remove</button>
          </div>
        ))}
        <button type="button" className="text-xs text-indigo-700 hover:underline mt-1"
          onClick={() => setRows([...rows, { key: '', delta: '' }])}>+ add limit increase</button>
        {limitKeys.length === 0 && <p className="text-xs text-amber-700 mt-1">Loading canonical limit keys from the Feature Catalog…</p>}
      </div>
    );
  };

  // Generic boolean feature-grant editor → commercial_add_on_versions.feature_grants ["feature_key", …]. Options
  // come ONLY from the canonical Feature Catalog (boolean features) — no hardcoded key. A published version granting
  // these makes the tenant entitled to the feature (e.g. addon_tracking_sms grants tracking_sms). Plain render fn.
  const featureGrantsEditor = () => {
    const sel = fields.feature_grants || [];
    const toggle = (k) => setFields((f) => {
      const cur = f.feature_grants || [];
      return { ...f, feature_grants: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] };
    });
    return (
      <div className="mt-3 border-t pt-2">
        <div className="text-sm font-semibold text-gray-700 mb-1">Feature grants <span className="font-normal text-gray-400">(boolean capabilities this add-on unlocks)</span></div>
        {featureGrantKeys.length === 0
          ? <p className="text-xs text-amber-700">Loading boolean features from the Feature Catalog…</p>
          : (
            <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {featureGrantKeys.map((o) => (
                <label key={o.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={sel.includes(o.key)} onChange={() => toggle(o.key)} />
                  <span>{o.label} <span className="text-gray-400 font-mono text-xs">({o.key})</span></span>
                </label>
              ))}
            </div>
          )}
        {sel.length > 0 && <p className="text-xs text-gray-500 mt-1">Granting: {sel.join(', ')}</p>}
      </div>
    );
  };

  // USAGE_PACK (capacity-limit) authoring fields — a plain render function (not a component) so typing
  // never remounts the inputs. `includeKey` shows the immutable product_key only on CREATE.
  const usagePackFieldsGrid = (includeKey) => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {includeKey && <label className="block text-xs text-gray-700">Product key *{input('product_key')}</label>}
        <label className="block text-xs text-gray-700">Display name{input('display_name')}</label>
        <label className="block text-xs text-gray-700">Capacity limit_key *{selectField('limit_key', USAGE_LIMIT_KEYS)}</label>
        <label className="block text-xs text-gray-700">Unit{input('unit', { placeholder: 'engagements / resolutions / staff …' })}</label>
        <label className="block text-xs text-gray-700">Granted quantity *{input('granted_quantity', { type: 'number' })}</label>
        <label className="block text-xs text-gray-700">Price (cents){input('price', { type: 'number' })}</label>
        <label className="block text-xs text-gray-700">Currency{input('currency')}</label>
        <label className="block text-xs text-gray-700">Expiry (months){input('expiry_months', { type: 'number' })}</label>
        <label className="block text-xs text-gray-700 md:col-span-2">Eligible plan versions <span className="text-gray-400">(comma-separated)</span>{input('eligible_plan_versions', { placeholder: 'professional@v2, enterprise@v1' })}</label>
        <label className="block text-xs text-gray-700">Eligible profiles <span className="text-gray-400">(comma-separated)</span>{input('eligible_profiles', { placeholder: 'clinic, movers' })}</label>
        <label className="block text-xs text-gray-700 md:col-span-3">Description{input('description')}</label>
      </div>
      <label className="block text-xs text-gray-700 mt-2">Reason <span className="text-gray-400">(audited)</span>{input('reason')}</label>
    </>
  );

  // Stripe TEST mapping form — attaches an existing Stripe TEST product/price to a governed version.
  const stripeMapPanel = (scope, key, version) => (
    <div className="border rounded p-3 mt-2 bg-indigo-50/50">
      <div className="text-xs font-semibold text-indigo-800 mb-2">Stripe TEST mapping — v{version} <span className="font-normal text-indigo-600">(TEST-mode reference only; creates no Stripe object)</span></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-700">Stripe TEST product id *{input('stripe_test_product_id', { placeholder: 'prod_…' })}</label>
        <label className="block text-xs text-gray-700">Stripe TEST price id *{input('stripe_test_price_id', { placeholder: 'price_…' })}</label>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
        <Button size="sm" disabled={busy} onClick={() => submitStripeMap(scope, key, version)}>{busy ? 'Saving…' : 'Save Stripe TEST mapping'}</Button>
      </div>
    </div>
  );

  // A plain render FUNCTION (not a component) so typing never remounts the inputs / loses focus. Rendered inside
  // a roomy modal (see the version dialog at the end of the component).
  const renderVersionForm = (onSubmit, submitLabel) => (
    <div className="text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
        <label className="block text-sm text-gray-700">Add-on type
          <select className="mt-1 w-full border rounded px-3 py-2 text-sm" value={fields.addon_type ?? ''} onChange={(e) => setFields((f) => ({ ...f, addon_type: e.target.value }))}>
            <option value="">— select —</option>{ADDON_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block text-sm text-gray-700">Billing model
          <select className="mt-1 w-full border rounded px-3 py-2 text-sm" value={fields.billing_model ?? ''} onChange={(e) => setFields((f) => ({ ...f, billing_model: e.target.value }))}>
            <option value="">— select —</option>{BILLING_MODELS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block text-sm text-gray-700">Billing cadence{input('billing_cadence', { placeholder: 'monthly / annual / one_time' })}</label>
        <label className="block text-sm text-gray-700">Currency{input('currency')}</label>
        <label className="block text-sm text-gray-700">Unit price (cents){input('unit_price_cents', { type: 'number' })}</label>
        <label className="block text-sm text-gray-700">Annual price (cents) <span className="text-gray-400">optional</span>{input('annual_price_cents', { type: 'number' })}</label>
        <label className="block text-sm text-gray-700 md:col-span-2">Eligible plan ids <span className="text-gray-400">(comma-separated)</span>{input('eligible_plan_ids', { placeholder: 'professional, enterprise' })}</label>
        <label className="block text-sm text-gray-700">Effective at <span className="text-gray-400">optional</span>{input('effective_at', { placeholder: 'ISO 8601' })}</label>
        <label className="block text-sm text-gray-700 md:col-span-2">Public description{input('public_description')}</label>
      </div>
      {limitGrantsEditor()}
      {featureGrantsEditor()}
      <div className="flex flex-wrap gap-4 mt-3">
        {check('tenant_requestable', 'Tenant requestable')}
        {check('manual_invoice_eligible', 'Manual invoice eligible')}
        {check('self_service_purchasable', 'Self-service purchasable')}
      </div>
      <label className="block text-sm text-gray-700 mt-3">Reason <span className="text-gray-400">(audited)</span>{input('reason')}</label>
      <div className="flex justify-end gap-2 mt-4">
        <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
        <Button size="sm" disabled={busy} onClick={onSubmit}>{busy ? 'Saving…' : submitLabel}</Button>
      </div>
    </div>
  );

  const packGroups = packVersions.reduce((acc, v) => { (acc[v.pack_key] = acc[v.pack_key] || []).push(v); return acc; }, {});
  const usagePackGroups = usagePackVersions.reduce((acc, v) => { const k = v.product_key || v.pack_key; (acc[k] = acc[k] || []).push(v); return acc; }, {});

  return (
    <div className="space-y-5">
      {gated && (
        <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800" role="status">
          Commercial write actions are not enabled in this environment. The control is governed and available,
          but activation is gated until the coordinated release enables it.
        </div>
      )}
      {toast && <div className={`p-2 rounded text-sm ${toast.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{toast.msg}</div>}
      {error && <div className="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700" role="alert">{error}</div>}

      {/* ── ADD-ONS ──────────────────────────────────────────────────────────── */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Add-ons (governed authoring)</CardTitle>
          <Button size="sm" disabled={busy} onClick={() => openForm('add-addon', {}, emptyAddon)}>+ Add Add-on</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {form?.kind === 'add-addon' && (
            <div className="border rounded p-3 mb-3 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <label className="block text-xs text-gray-700">Internal id/key *{input('id')}</label>
                <label className="block text-xs text-gray-700">Display name{input('display')}</label>
                <label className="block text-xs text-gray-700">Price (cents) <span className="text-gray-400">optional</span>{input('price_cents', { type: 'number' })}</label>
                <label className="block text-xs text-gray-700">Cadence{input('cadence')}</label>
              </div>
              <label className="block text-xs text-gray-700 mt-2">Reason <span className="text-gray-400">(audited)</span>{input('reason')}</label>
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
                <Button size="sm" disabled={busy} onClick={submitAddon}>{busy ? 'Saving…' : 'Create draft add-on'}</Button>
              </div>
            </div>
          )}
          {loading ? <p className="text-sm text-gray-400">Loading add-ons…</p>
            : addOns.length === 0 ? <p className="text-sm text-gray-500">No add-ons yet. Use “Add Add-on” to create a governed DRAFT header.</p>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>{['Add-on', 'Id', 'Header price', 'Cadence', 'Effective / latest version', 'Actions'].map(th)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {addOns.map((a) => (
                    <React.Fragment key={a.id}>
                      <tr>
                        <td className="px-2 py-1 font-medium text-gray-900">{primaryLabel(a.display, a.id)}</td>
                        <td className="px-2 py-1 font-mono text-xs">{a.id}</td>
                        <td className="px-2 py-1">{(() => {
                          // Header price is a summary: a priced product carries its price on the EFFECTIVE VERSION
                          // (unit_price_cents), not the header. Show the effective version's price when the header
                          // has none, so a published/effective priced add-on never falsely reads "—".
                          const eff = addonEffective(a.id)?.eff;
                          const cents = a.price_cents != null ? a.price_cents : (eff ? eff.unit_price_cents : null);
                          return cents != null ? usd(cents) : '—';
                        })()}</td>
                        <td className="px-2 py-1">{a.cadence || addonEffective(a.id)?.eff?.billing_cadence || '—'}</td>
                        <td className="px-2 py-1">
                          {(() => {
                            const info = addonEffective(a.id);
                            if (!info) return <span className="text-xs text-gray-400">…</span>;
                            const showLatest = info.latest && (!info.eff || info.latest.version !== info.eff.version);
                            return (
                              <div className="flex flex-col gap-0.5">
                                {info.eff
                                  ? <span className="text-xs whitespace-nowrap"><span className="text-gray-500">Effective </span>v{info.eff.version} <Pill v="published" /></span>
                                  : <span className="text-xs text-amber-700 whitespace-nowrap">No published version</span>}
                                {showLatest && (
                                  <span className="text-xs whitespace-nowrap"><span className="text-gray-500">Latest </span>v{info.latest.version} <Pill v={info.latest.lifecycle_state} /></span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex flex-wrap gap-1">
                            <button disabled={busy} onClick={() => toggleVersions(a.id)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">{expanded.has(a.id) ? 'Hide versions' : 'Versions'}</button>
                            <button disabled={busy} onClick={() => openForm('add-version', { addOnId: a.id }, { ...emptyVersion })} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Add Version</button>
                          </div>
                        </td>
                      </tr>
                      {expanded.has(a.id) && (
                        <tr><td colSpan={6} className="px-2 py-2 bg-gray-50/40">
                          {!addOnVersions[a.id] ? <p className="text-xs text-gray-400">Loading versions…</p>
                            : addOnVersions[a.id].length === 0 ? <p className="text-xs text-gray-500">No versions yet — use “Add Version”.</p>
                            : (
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 border-b"><tr>{['Version', 'Lifecycle', 'Billing model', 'Unit price', 'Cadence', 'Eligible plans', 'Actions'].map(th)}</tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                  {addOnVersions[a.id].map((v) => {
                                    const aa = v.allowed_actions || {};
                                    const vkey = `a:${a.id}:${v.version}`;
                                    return (
                                      <tr key={v.add_on_version_id || v.version}>
                                        <td className="px-2 py-1">v{v.version}</td>
                                        <td className="px-2 py-1"><Pill v={v.lifecycle_state} /></td>
                                        <td className="px-2 py-1">{v.billing_model || '—'}</td>
                                        <td className="px-2 py-1">{usd(v.unit_price_cents)}</td>
                                        <td className="px-2 py-1">{v.billing_cadence || '—'}</td>
                                        <td className="px-2 py-1 text-xs">{(v.eligible_plan_ids || []).join(', ') || '—'}</td>
                                        <td className="px-2 py-1">
                                          <div className="flex flex-wrap gap-1">
                                            {aa.edit && <button disabled={busy} onClick={() => openForm('edit-version', { addOnId: a.id, version: v.version }, {
                                              addon_type: v.addon_type || '', billing_model: v.billing_model || '', billing_cadence: v.billing_cadence || '',
                                              currency: v.currency || 'USD', unit_price_cents: v.unit_price_cents ?? '', annual_price_cents: v.annual_price_cents ?? '',
                                              eligible_plan_ids: (v.eligible_plan_ids || []).join(', '), tenant_requestable: !!v.tenant_requestable,
                                              manual_invoice_eligible: !!v.manual_invoice_eligible, self_service_purchasable: !!v.self_service_purchasable,
                                              public_description: v.public_description || '', effective_at: v.effective_at || '',
                                              limit_grants_rows: Object.entries(v.limit_grants || {}).map(([k, d]) => ({ key: k, delta: String(d) })),
                                              feature_grants: Array.isArray(v.feature_grants) ? v.feature_grants : [], reason: '',
                                            })} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Edit Draft</button>}
                                            {aa.validate && <button disabled={busy} onClick={() => validateAddonVersion(a.id, v.version)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Validate</button>}
                                            <LifecycleButtons allowed={aa} onAct={(k) => addonLifecycle(a.id, v.version, k)} />
                                          </div>
                                          {aa.immutable && <div className="mt-1 text-xs text-amber-700">{READ_ONLY_MSG}</div>}
                                          <ValidateResult res={validateResults[vkey]} />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          <p className="text-xs text-gray-400 mt-2">New headers and versions default to DRAFT. Lifecycle actions are the governed transitions the backend allows for each version — nothing auto-publishes and no Stripe object is created.</p>
        </CardContent>
      </Card>

      {/* ── CREDIT PACKS (prepaid wallet credits) ────────────────────────────── */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Credit Packs (governed credit-pack versions)</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => openForm('add-pack', {}, emptyPack)}>+ Add Credit Pack</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={importSeedPacks}>Import seed credit packs</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">Prepaid <b>wallet credits</b> (e.g. AI operations / communication). For capacity ceilings, use the Usage Packs card below.</p>
          {form?.kind === 'add-pack' && (
            <div className="border rounded p-3 mb-3 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="block text-xs text-gray-700">Pack key *{input('pack_key')}</label>
                <label className="block text-xs text-gray-700">Bucket{input('bucket', { placeholder: 'ai_operations / communication' })}</label>
                <label className="block text-xs text-gray-700">Credits{input('credits', { type: 'number' })}</label>
                <label className="block text-xs text-gray-700">Price (cents){input('price', { type: 'number' })}</label>
                <label className="block text-xs text-gray-700">Currency{input('currency')}</label>
                <label className="block text-xs text-gray-700">Expiry (months){input('expiry_months', { type: 'number' })}</label>
              </div>
              <label className="block text-xs text-gray-700 mt-2">Reason <span className="text-gray-400">(audited)</span>{input('reason')}</label>
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
                <Button size="sm" disabled={busy} onClick={submitPack}>{busy ? 'Saving…' : 'Create governed draft version'}</Button>
              </div>
            </div>
          )}
          {loading ? <p className="text-sm text-gray-400">Loading credit packs…</p>
            : Object.keys(packGroups).length === 0 ? <p className="text-sm text-gray-500">No governed credit-pack versions yet. Use “Add Credit Pack” or “Import seed credit packs”.</p>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>{['Pack', 'Version', 'Lifecycle', 'Bucket', 'Credits', 'Price', 'Currency', 'Expiry (months)', 'Actions'].map(th)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(packGroups).map(([pk, vs]) => vs.map((v, i) => {
                    const aa = v.allowed_actions || {};
                    const vkey = `p:${pk}:${v.version}`;
                    return (
                      <tr key={vkey}>
                        <td className="px-2 py-1 font-mono text-xs">{i === 0 ? pk : ''}</td>
                        <td className="px-2 py-1">v{v.version}</td>
                        <td className="px-2 py-1"><Pill v={v.lifecycle_state} /></td>
                        <td className="px-2 py-1">{v.bucket || '—'}</td>
                        <td className="px-2 py-1">{v.credits ?? '—'}</td>
                        <td className="px-2 py-1">{v.price != null ? usd(v.price) : '—'}</td>
                        <td className="px-2 py-1">{v.currency || '—'}</td>
                        <td className="px-2 py-1">{v.expiry_months ?? '—'}</td>
                        <td className="px-2 py-1">
                          <div className="flex flex-wrap gap-1">
                            {aa.validate && <button disabled={busy} onClick={() => validatePackVersion(pk, v.version)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Validate</button>}
                            <LifecycleButtons allowed={aa} onAct={(k) => packLifecycle(pk, v.version, k)} />
                            <button disabled={busy} onClick={() => openForm('stripe-map', { scope: 'credit-packs', key: pk, version: v.version }, {
                              ...emptyStripeMap,
                              stripe_test_product_id: v.stripe_test_mapping?.stripe_test_product_id || '',
                              stripe_test_price_id: v.stripe_test_mapping?.stripe_test_price_id || '',
                            })} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Stripe TEST map</button>
                          </div>
                          {aa.immutable && <div className="mt-1 text-xs text-amber-700">{READ_ONLY_MSG}</div>}
                          <ValidateResult res={validateResults[vkey]} />
                          {form?.kind === 'stripe-map' && form.scope === 'credit-packs' && form.key === pk && form.version === v.version && stripeMapPanel('credit-packs', pk, v.version)}
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            )}
          <p className="text-xs text-gray-400 mt-2">There is no in-place edit for a pack version — create a new draft version instead. Publishing revalidates and makes the version immutable; checkout runtime authority is unchanged here. Stripe TEST mapping references an existing TEST product/price only — no Stripe object is created.</p>
        </CardContent>
      </Card>

      {/* ── USAGE PACKS (CAPACITY-LIMIT, not a credit wallet) ─────────────────── */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Usage Packs — capacity-limit (governed)</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => openForm('add-usage-pack', {}, { ...emptyUsagePack })}>+ Add Usage Pack</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={importSeedUsagePacks}>Import seed packs</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">A usage pack raises a metered <b>capacity ceiling</b> (a <span className="font-mono">limit_key</span> such as monthly engagements) — it is <b>NOT</b> a credit wallet and grants no spendable balance. “Import seed packs” seeds the two approved packs (<span className="font-mono">pack_engagements_1000</span>, <span className="font-mono">pack_ai_250</span>) as governed DRAFTs.</p>
          {form?.kind === 'add-usage-pack' && (
            <div className="border rounded p-3 mb-3 bg-gray-50">
              {usagePackFieldsGrid(true)}
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
                <Button size="sm" disabled={busy} onClick={submitUsagePack}>{busy ? 'Saving…' : 'Create governed draft version'}</Button>
              </div>
            </div>
          )}
          {loading ? <p className="text-sm text-gray-400">Loading usage packs…</p>
            : Object.keys(usagePackGroups).length === 0 ? <p className="text-sm text-gray-500">No governed usage-pack versions yet. Use “Add Usage Pack” or “Import seed packs”.</p>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>{['Pack', 'Version', 'Lifecycle', 'Limit key', 'Unit', 'Granted', 'Price', 'Currency', 'Expiry (months)', 'Actions'].map(th)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(usagePackGroups).map(([pk, vs]) => vs.map((v, i) => {
                    const aa = v.allowed_actions || {};
                    const vkey = `u:${pk}:${v.version}`;
                    return (
                      <tr key={vkey}>
                        <td className="px-2 py-1 font-mono text-xs">{i === 0 ? pk : ''}</td>
                        <td className="px-2 py-1">v{v.version}</td>
                        <td className="px-2 py-1"><Pill v={v.lifecycle_state} /></td>
                        <td className="px-2 py-1 font-mono text-xs">{v.limit_key || '—'}</td>
                        <td className="px-2 py-1">{v.unit || '—'}</td>
                        <td className="px-2 py-1">{v.granted_quantity ?? '—'}</td>
                        <td className="px-2 py-1">{v.price != null ? usd(v.price) : '—'}</td>
                        <td className="px-2 py-1">{v.currency || '—'}</td>
                        <td className="px-2 py-1">{v.expiry_months ?? '—'}</td>
                        <td className="px-2 py-1">
                          <div className="flex flex-wrap gap-1">
                            {aa.edit && <button disabled={busy} onClick={() => openForm('edit-usage-pack', { productKey: pk, version: v.version }, {
                              ...emptyUsagePack, product_key: pk, display_name: v.display_name || '', description: v.description || '',
                              limit_key: v.limit_key || '', unit: v.unit || '', granted_quantity: v.granted_quantity ?? '',
                              price: v.price ?? '', currency: v.currency || 'USD', expiry_months: v.expiry_months ?? '',
                              eligible_plan_versions: (v.eligible_plan_versions || []).join(', '), eligible_profiles: (v.eligible_profiles || []).join(', '), reason: '',
                            })} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Edit Draft</button>}
                            {aa.validate && <button disabled={busy} onClick={() => validateUsagePackVersion(pk, v.version)} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Validate</button>}
                            <LifecycleButtons allowed={aa} onAct={(k) => usagePackLifecycle(pk, v.version, k)} />
                            <button disabled={busy} onClick={() => openForm('stripe-map', { scope: 'usage-packs', key: pk, version: v.version }, {
                              ...emptyStripeMap,
                              stripe_test_product_id: v.stripe_test_mapping?.stripe_test_product_id || '',
                              stripe_test_price_id: v.stripe_test_mapping?.stripe_test_price_id || '',
                            })} className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">Stripe TEST map</button>
                          </div>
                          {aa.immutable && <div className="mt-1 text-xs text-amber-700">{READ_ONLY_MSG}</div>}
                          <ValidateResult res={validateResults[vkey]} />
                          {form?.kind === 'edit-usage-pack' && form.productKey === pk && form.version === v.version && (
                            <div className="mt-2 border rounded p-3 bg-gray-50">
                              {usagePackFieldsGrid(false)}
                              <div className="flex justify-end gap-2 mt-2">
                                <Button size="sm" variant="outline" disabled={busy} onClick={closeForm}>Cancel</Button>
                                <Button size="sm" disabled={busy} onClick={() => submitEditUsagePack(pk, v.version)}>{busy ? 'Saving…' : 'Save draft'}</Button>
                              </div>
                            </div>
                          )}
                          {form?.kind === 'stripe-map' && form.scope === 'usage-packs' && form.key === pk && form.version === v.version && stripeMapPanel('usage-packs', pk, v.version)}
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            )}
          <p className="text-xs text-gray-400 mt-2">Capacity-limit packs default to DRAFT and follow the same governed lifecycle (draft → under_review → approved → scheduled → published). Published versions are immutable — create a new draft to change one. No Stripe object is created.</p>
        </CardContent>
      </Card>

      {/* Lifecycle reason modal (no native prompt). Reason required + audited server-side. */}
      <Dialog open={!!reasonModal} onOpenChange={(o) => { if (!o) { setReasonModal(null); setReasonText(''); setReasonErr(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Governed lifecycle transition</DialogTitle>
            <DialogDescription>{reasonModal?.title}</DialogDescription>
          </DialogHeader>
          <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">Reason (recorded in audit) <span className="text-red-500">*</span></label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Why is this transition being made?" />
          {reasonErr && <p className="text-sm text-red-700 mt-2" role="alert">{reasonErr}</p>}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" size="sm" onClick={() => { setReasonModal(null); setReasonText(''); setReasonErr(null); }}>Cancel</Button>
            <Button size="sm" onClick={confirmReason}>Apply transition</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit add-on VERSION — a roomy governed modal (replaces the old cramped in-table form). */}
      <Dialog open={form?.kind === 'add-version' || form?.kind === 'edit-version'}
              onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent className="max-w-3xl w-[92vw] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?.kind === 'edit-version' ? `Edit draft version v${form?.version}` : 'New add-on version'}</DialogTitle>
            <DialogDescription>{form?.addOnId}{form?.kind === 'add-version' ? ' — new draft (not published on create)' : ''}</DialogDescription>
          </DialogHeader>
          {(form?.kind === 'add-version' || form?.kind === 'edit-version') && renderVersionForm(
            () => (form.kind === 'add-version' ? submitVersion(form.addOnId) : submitEditVersion(form.addOnId, form.version)),
            form.kind === 'add-version' ? 'Create draft version' : 'Save draft',
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
